// src/api/onboarding-scoring-api.ts
//
// API client for the configurable scoring + human-review (Kappa) workflow.
// Sits alongside onboarding-api.ts and reuses its apiFetch + downloader.
//
import { apiFetch } from "./api-fetch";
import { buildWsUrl } from "./url-route";
import { downloadExportFile, type CustomerDetail } from "./onboarding-api";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface ScorerModel {
  id: string;
  label: string;
  provider: string;
  model_string: string;
  recommended: boolean;
  is_default: boolean;
  /** e.g. { "temperature": "omit" } — surfaced in the UI as a constraint note. */
  param_constraints: Record<string, unknown>;
  notes: string;
}

export interface ScoringCounts {
  total: number;
  scored: number;
  failed_zeroed: number;
  /** Rows whose measurement call returned no answer (errored, or an empty AI
   *  Mode answer). Kept for the audit trail, outside every rate. */
  excluded_no_answer?: number;
  mention_positive: number;
}

export interface ReviewSessionSummary {
  id: number;
  reviewer: string;
  status: "in_review" | "completed";
  sample_size: number;
  overall_kappa: number | null;
  created_at: string | null;
}

/** One scoring pass on a run. A run judged twice keeps BOTH — switching which
 *  one the reports read is a pointer change, never a second judge pass. */
export interface ScoringPass {
  id: number;
  scorer_model: string;
  rubric_version: string;
  stage4_hash: string | null;
  /** The pass this run's reports, Deep Signal and cleanroom export read. */
  is_active: boolean;
  /** 'live' — written when the judge pass finished.
   *  'rebuilt' — re-aggregated from the stored score rows (no token figures).
   *  'adopted' — recorded when Stage 5 adopted a pass it did not run. */
  summary_origin: "live" | "rebuilt" | "adopted" | string;
  responses_scored: number | null;
  failed_zeroed: number | null;
  /** null = a pass recorded before the field existed, not "none". */
  excluded_no_answer?: number | null;
  /** How this pass was SENT. null = not recorded, which is the honest answer
   *  for a rebuilt pass: the transport leaves no trace in a score row. It is
   *  what explains two passes on one run costing very different amounts. */
  scoring_mode: ScoringMode | null;
  /** null = not measurable (a rebuilt pass) — never "free". */
  cost_usd_measured: number | null;
  scored_at: string | null;
  created_at: string | null;
}

/**
 * How a Stage-5 scoring pass is SENT.
 *
 *   "batch"  - Anthropic Message Batches: 50% of standard prices, results when
 *              the batch ends (usually under an hour, guaranteed within 24).
 *   "direct" - one synchronous request per response: full price, minutes.
 *
 * Same judge, same rubric, same repeats, same request body either way. This is
 * a cost-and-latency decision and never a measurement one.
 *
 * Defined with the run row (onboarding-api), which also carries it, and
 * re-exported here so existing importers are unchanged. One declaration: the
 * run list and the status poll describe the same transport and must not be
 * able to drift to two different unions of it.
 */
export type { ScoringMode, RunScoringStatus } from "./onboarding-api";
import type { ScoringMode, RunScoringStatus } from "./onboarding-api";

export interface ScoringState {
  run_id: number;
  default_scorer_model: string;
  suggested_rubric_version: string;
  /** A complete, current scoring pass already exists that Stage 5 can adopt
   *  (advance to 6) without re-judging. */
  adoptable: boolean;
  /** The judge `adoptable` was decided against: this run's pinned judge, or the
   *  registry default for a run that has none. An `adoptable: false` is
   *  undiagnosable without it — a judge mismatch and a framework-drifted rubric
   *  look identical from the outside. */
  adoptable_scorer_model_id: string;
  /** The rubric `adoptable` was decided against — derived from the CURRENT
   *  framework, so a framework edit still (deliberately) requires a re-judge. */
  adoptable_rubric_version: string;
  /** The judge Stage 4 pinned for this run, which the auto-pipeline honours.
   *  null on runs configured before the field existed. */
  configured_scorer_model_id: string | null;
  /** The scoring transport Stage 4 pinned for this run, if one was chosen
   *  explicitly. null = no preference, so the server decides. */
  configured_scoring_mode: ScoringMode | null;
  /** What the server falls back to when nothing was chosen. */
  default_scoring_mode: ScoringMode;
  /** What choosing Direct costs relative to Batch (batched tokens bill at
   *  half, so this is 2). Server-supplied so the label is never a second copy
   *  of a number that can drift from the pricing code. */
  direct_mode_cost_multiplier: number;
  /** Raw responses collected — the row count a COMPLETE pass has. */
  expected_rows: number;
  /** A judge pass is running right now (manual re-score or auto-pipeline).
   *  Stage 5 polls while this is true so it never renders a stale row count. */
  scoring_in_flight: boolean;
  /** The same block every run row in the run list carries, so both screens
   *  describe one state in one vocabulary. `scoring.in_flight` IS
   *  `scoring_in_flight` above — one backend call, not a second opinion. */
  scoring: RunScoringStatus;
  passes: ScoringPass[];
  active_pass: ScoringPass | null;
  /** The active pass's full summary, so a polling Stage 5 can render the
   *  finished result the moment an auto-pipeline pass lands. */
  active_summary: ScoringSummary | null;
  counts_by_pass: (ScoringCounts & {
    scorer_model: string;
    rubric_version: string;
  })[];
  /** Rows of the ACTIVE pass. `counts` below sums every row on the run, which
   *  on a twice-judged run describes no pass at all. */
  active_counts: ScoringCounts;
  counts: ScoringCounts;
  review_sessions: ReviewSessionSummary[];
}

/** Real, token-derived spend for a scoring pass — the number the Anthropic
 *  console bills, as opposed to the flat per-call estimate. */
export interface TokenUsage {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  /** Cache writes split by TTL — they bill at different rates (1.25x base
   *  input for a 5-minute entry, 2.00x for a one-hour one). A non-zero 1h
   *  figure is the observable proof the extended TTL is live. Absent on
   *  summaries written before #84. */
  cache_creation_5m_input_tokens?: number;
  cache_creation_1h_input_tokens?: number;
  cache_hit_rate: number;
  web_search_requests: number;
  /** null = no authoritative price for this scorer ("unknown", never "free"). */
  cost_usd_measured: number | null;
  /** The same tokens at standard prices — what this pass would have cost sent
   *  directly. Equal to cost_usd_measured when nothing was batched. */
  cost_usd_standard_equivalent?: number | null;
  /** How many of `calls` went through the Message Batches API at half price. */
  batch_calls?: number;
  batch_input_tokens?: number;
  batch_output_tokens?: number;
  batch_discount_applied?: boolean;
  priced: boolean;
}

export interface ScoringSummary {
  mention_rate: number;
  top5_rate: number;
  primary_rec_rate: number;
  number_one_rate: number;
  per_model: Record<string, Record<string, number>>;
  per_reasoning_variable: Record<string, number>;
  responses_scored: number;
  failed_zeroed: number;
  /** Rows outside every rate: the measurement call returned no answer. */
  excluded_no_answer?: number;
  scorer_model: string;
  scorer_provider: string;
  rubric_version: string;
  /** What this pass actually cost, from the providers' usage objects. THE
   *  cost figure. null when it cannot be measured (a rebuilt summary). */
  scoring_cost_usd_measured?: number | null;
  /** On summaries before 2026-10-30, NOT a cost: calls x a flat per-call
   *  constant, identical whichever judge ran ($75.00 on both run 29 and run
   *  32). Newer summaries set it equal to scoring_cost_usd_measured. Render
   *  spend from scoring_cost_usd_measured (or token_usage.cost_usd_measured on
   *  older summaries), never from this. */
  scoring_cost_usd_est: number | null;
  cost_basis?: {
    measured: number | null;
    /** Present only on summaries before 2026-10-30. */
    flat_estimate?: number | null;
    note: string;
  };
  /** Token-measured spend + cache stats. Absent on very old summaries. */
  token_usage?: TokenUsage;
  /** Rows zeroed because the judge's reply never parsed, even after retries. */
  parse_failures?: number;
  /** How this pass was SENT, and why. Recorded rather than inferred: "why did
   *  this run cost twice the last one" is asked about a bill weeks later, by
   *  which time the defaults have moved on. Absent on pre-#84 summaries. */
  scoring_mode?: ScoringMode;
  scoring_mode_reason?: string;
  /** Batched requests that came back errored or expired. Each became an
   *  explicit zeroed row — never a silent gap — so a non-zero batch_expired
   *  means a "complete" pass was only mostly delivered. */
  batch_errored?: number;
  batch_expired?: number;
  /** Present and 'rebuilt' when this summary was re-aggregated from stored
   *  score rows rather than written by a live pass. */
  summary_origin?: string;
  scored_at: string;
  stage4_hash?: string;
  stage5_hash?: string;
}

/** The measured spend for a pass, or null when it was never measured.
 *  Deliberately does NOT fall back to scoring_cost_usd_est: that number is a
 *  judge-independent placeholder, and showing it as a cost is how $75.00
 *  appeared against an $11.39 pass. */
export function measuredScoringCost(
  summary: Pick<
    ScoringSummary,
    "scoring_cost_usd_measured" | "token_usage"
  > | null | undefined,
): number | null {
  if (!summary) return null;
  if (summary.scoring_cost_usd_measured != null) {
    return summary.scoring_cost_usd_measured;
  }
  return summary.token_usage?.cost_usd_measured ?? null;
}

export interface ResponseScore {
  response_index: number;
  model: string;
  query_text: string;
  scorer_model: string;
  rubric_version: string;
  scorer_mentioned: boolean;
  scorer_in_top5: boolean;
  scorer_primary_rec: boolean;
  scorer_number_one: boolean;
  rv_scores: Record<string, number>;
  rv_evidence: Record<string, string>;
  mention_evidence: string;
  scoring_status: "scored" | "failed_zeroed" | "excluded_no_answer";
  error_message: string | null;
}

export interface ReviewItem {
  id: number;
  response_index: number;
  model: string;
  stratum: "mention_positive" | "control";
  query_text: string;
  response_text: string;
  scorer_mentioned: boolean;
  scorer_rv_scores: Record<string, number>;
  scorer_rv_evidence: Record<string, string>;
  human_mentioned: boolean | null;
  human_rv_scores: Record<string, number>;
  reviewer_notes: string;
  reviewed: boolean;
}

export interface ReviewSessionDetail {
  session: {
    id: number;
    run_id: number;
    customer_id: number;
    reviewer: string;
    status: "in_review" | "completed";
    sample_seed: number;
    sample_size: number;
    mention_positive_count: number;
    control_count: number;
    scorer_model_version: string;
    rubric_version: string;
    per_variable_kappa: Record<
      string,
      { kappa: number | null; n: number; agreement: number; strength: string }
    >;
    overall_kappa: number | null;
    mention_kappa: number | null;
    notes: string;
    reviewed_count: number;
  };
  reasoning_variables: { id: string; claim: string }[];
  items: ReviewItem[];
}

export interface KappaResult {
  session_id: number;
  per_variable_kappa: Record<
    string,
    { kappa: number | null; n: number; agreement: number; strength: string }
  >;
  overall_kappa: number | null;
  overall_strength: string;
  mention_kappa: number | null;
  mention_strength: string;
  reviewed_count: number;
  total_count: number;
  finalised: boolean;
}

// ─── Calls ──────────────────────────────────────────────────────────────────

export async function listScorerModels(): Promise<{ models: ScorerModel[] }> {
  return apiFetch("/onboarding/scoring/models");
}

export async function getScoringState(
  customerId: number,
  runId: number,
): Promise<ScoringState> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/scoring/state`,
  );
}

// ─── Async scoring job (Celery-backed, WS-streamed) ─────────────────────────

export type ScoringRunStatus =
  | "pending"
  | "running"
  | "completed"
  | "cancelled"
  | "failed";

export interface ScoringRun {
  id: number;
  measurement_run_id: number;
  customer_id: number;
  status: ScoringRunStatus;
  task_id: string | null;
  scorer_model_id: string;
  scorer_model_string: string;
  rubric_version: string;
  max_concurrency: number;
  /** The transport this pass was explicitly started with, or null when the
   *  server decided. */
  scoring_mode: ScoringMode | null;
  progress_total: number;
  progress_completed: number;
  cost_so_far_usd: number;
  cancellation_requested: boolean;
  error_message: string | null;
  triggered_by_username: string | null;
  started_at: string | null;
  finished_at: string | null;
  summary: ScoringSummary | null;
}

/**
 * Result of POST /scoring/run. Either a background job was enqueued
 * (`scoring_run` set, `adopted` falsy) or — when a complete, current scoring
 * pass already existed and `force_rescore` was not set — the existing pass was
 * ADOPTED synchronously: no judge cost, stage advanced, `adopted: true` with
 * the reused summary + refreshed customer.
 */
export interface StartScoringResult {
  scoring_run: ScoringRun | null;
  adopted?: boolean;
  scoring_summary?: ScoringSummary;
  customer?: CustomerDetail;
}

/**
 * Enqueue a Stage-5 re-score as a background job. Returns immediately with
 * the new scoring_run record — open the WS to stream progress and the final
 * summary. Replaces the prior synchronous endpoint that timed out at nginx
 * for large measurement runs.
 *
 * If a complete, current scoring pass already exists and `force_rescore` is not
 * set, the existing pass is adopted instead (see StartScoringResult) — one
 * judge pass per run unless a human explicitly forces a re-score.
 */
export async function startScoringRun(
  customerId: number,
  runId: number,
  body: {
    scorer_model_id?: string;
    rubric_version?: string;
    max_concurrency?: number;
    force_rescore?: boolean;
    /** Omitted -> the server decides (batch for ordinary scoring). Set
     *  "direct" only when the turnaround is worth paying about twice for. */
    scoring_mode?: ScoringMode;
  },
): Promise<StartScoringResult> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/scoring/run`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Every scoring pass recorded for a run, active first. */
export async function listScoringPasses(
  customerId: number,
  runId: number,
): Promise<{ passes: ScoringPass[] }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/scoring/passes`,
  );
}

/**
 * Point the run at one of its already-recorded passes.
 *
 * Free and reversible: no judge call, no rows deleted, both passes still on
 * disk afterwards. 404s rather than re-judging when no such pass exists.
 */
export async function activateScoringPass(
  customerId: number,
  runId: number,
  body: { scorer_model: string; rubric_version?: string },
): Promise<{
  scoring_summary: ScoringSummary;
  passes: ScoringPass[];
  customer: unknown;
}> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/scoring/passes/activate`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/**
 * Snapshot of the in-flight scoring run for this measurement run, or the
 * most recent terminal one if nothing is active. Returns
 * {scoring_run: null} when no scoring has ever been enqueued.
 */
export async function getActiveScoringRun(
  customerId: number,
  runId: number,
): Promise<{ scoring_run: ScoringRun | null }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/scoring/active`,
  );
}

export async function cancelScoringRun(
  scoringRunId: number,
): Promise<{ scoring_run: ScoringRun }> {
  return apiFetch(`/onboarding/scoring/runs/${scoringRunId}/cancel`, {
    method: "POST",
  });
}

// ─── WebSocket ──────────────────────────────────────────────────────────────

export type ScoringRunWsMessage =
  | { type: "snapshot"; scoring_run: ScoringRun }
  | { type: "no_run" }
  | { type: "scoring_started"; scoring_run_id: number; measurement_run_id: number; scorer_model_id: string; scorer_model_string: string; rubric_version: string; total: number; timestamp: string }
  | { type: "scoring_progress"; scoring_run_id: number; completed: number; total: number; percent: number; cost_so_far: number; timestamp: string }
  | { type: "scoring_complete"; scoring_run_id: number; measurement_run_id: number; summary: ScoringSummary; customer: CustomerDetail; timestamp: string }
  | { type: "scoring_cancelled"; scoring_run_id: number; completed: number; total: number; cost_usd: number; timestamp: string }
  // The judge batch is slower than one worker pass's wait budget. Scoring is
  // NOT stuck and nothing is resubmitted: the batch keeps running at the
  // provider and a continuation picks the run back up. Without this the
  // progress bar simply stopped moving for minutes with nothing said.
  | { type: "scoring_awaiting_batch"; scoring_run_id: number; measurement_run_id: number; provider_batch_id: string; phase: string; batch_finished: number; batch_total: number; retry_in_sec: number; timestamp: string }
  | { type: "heartbeat"; scoring_run_id: number; timestamp: string }
  | { type: "error"; scoring_run_id?: number; message: string; trace?: string; timestamp?: string };

export function openScoringRunWs(args: {
  scoringRunId: number;
  onEvent: (msg: ScoringRunWsMessage) => void;
  onClose?: () => void;
  onError?: (e: Event) => void;
}): WebSocket {
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    "";
  const base = buildWsUrl();
  const params = new URLSearchParams({
    token,
    scoring_run_id: String(args.scoringRunId),
  });
  const url = `${base}/onboarding/ws/scoring?${params.toString()}`;
  const ws = new WebSocket(url);

  ws.onmessage = (e) => {
    try {
      args.onEvent(JSON.parse(e.data));
    } catch {
      // ignore malformed
    }
  };
  if (args.onClose) ws.onclose = args.onClose;
  if (args.onError) ws.onerror = args.onError;
  return ws;
}

export async function listResponseScores(
  customerId: number,
  runId: number,
  opts: {
    limit?: number;
    offset?: number;
    rubricVersion?: string;
    scorerModel?: string;
  } = {},
): Promise<{ items: ResponseScore[]; counts: ScoringCounts }> {
  const qs = new URLSearchParams();
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  if (opts.offset !== undefined) qs.set("offset", String(opts.offset));
  // Pin ONE scoring pass. A pass is identified by rubric AND judge: a run
  // rescored against a new rubric is separated by rubricVersion alone, but a
  // run judged twice on the SAME rubric (run 29: opus and sonnet, both
  // fw-58be7e3b) is not — without the judge it returns both passes' rows
  // interleaved, 2,500 rows for 1,250 responses.
  if (opts.rubricVersion) qs.set("rubric_version", opts.rubricVersion);
  if (opts.scorerModel) qs.set("scorer_model", opts.scorerModel);
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/scoring/scores${
      qs.toString() ? `?${qs}` : ""
    }`,
  );
}

/** What one answer searched, saw, opened, consulted and cited. A list is
 *  empty when the lane's API does not report that thing. `start`/`end` are
 *  character offsets into the stored answer text. Gemini sources were Google
 *  redirect links: `url` is the page, `source_url` the redirect as returned. */
export interface Retrieval {
  queries: { position: number; text: string }[];
  results: {
    query_position: number | null; rank: number; url: string;
    title: string | null; page_age: string | null; source_url?: string;
  }[];
  opens: { position: number; url: string }[];
  sources: { url: string; title: string | null }[];
  citations: {
    position: number; url: string; title: string | null; cited_text: string | null;
    start: number | null; end: number | null; source_url?: string;
  }[];
  truncated?: string[];
}

/** One stored answer's retrieval record; `retrieval` is null when it has none
 *  (web off, nothing retrieved, or collected before the record existed). */
export async function getResponseRetrieval(
  customerId: number,
  runId: number,
  responseIndex: number,
): Promise<{ response_index: number; response_text: string; retrieval: Retrieval | null }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/responses/${responseIndex}/retrieval`,
  );
}

/**
 * Stage 5 — download the full per-response scoring detail (every 0/1/2
 * variable score, the evidence sentence per variable, scorer model + rubric
 * version, and any failed_zeroed rows). Optionally pin one scorer version.
 */
export async function downloadScoringExport(
  customerId: number,
  runId: number,
  format: "json" | "csv" = "csv",
  scorerModel?: string,
  rubricVersion?: string,
): Promise<void> {
  const qs = new URLSearchParams({ format });
  if (scorerModel) qs.set("scorer_model", scorerModel);
  if (rubricVersion) qs.set("rubric_version", rubricVersion);
  return downloadExportFile(
    `/onboarding/customers/${customerId}/runs/${runId}/scoring/export?${qs}`,
    `stage5-scores-run-${runId}-customer-${customerId}.${format}`,
    format === "json" ? "application/json" : "text/csv",
  );
}

/**
 * Cleanroom export — SHA-256-verified ZIP containing the exact Stage-4 and
 * Stage-5 JSON bytes plus HASHES.json and SUMMARY.txt. POST as specified.
 */
export async function downloadCleanroomExport(
  runId: number,
): Promise<void> {
  return downloadExportFile(
    `/onboarding/runs/${runId}/cleanroom-export`,
    `cleanroom-run-${runId}.zip`,
    "application/zip",
    "POST",
  );
}

// ─── Pre-deployment validation + content deployment + MANIFEST ──────────────

export interface PredeploymentPerVariable {
  arm_a: number | null;
  arm_b: number | null;
  delta: number | null;
  status: "PASS" | "WARN" | "FAIL" | "n/a";
}

export interface PredeploymentValidation {
  validation_id: number;
  run_id: number;
  verdict: "PASS" | "CONDITIONAL" | "FAIL";
  fail_vars: string[];
  warn_vars: string[];
  per_variable: Record<string, PredeploymentPerVariable>;
  arm_a_n: number;
  arm_b_n: number;
  scorer_model: string;
  rubric_version: string;
  report_text: string;
}

/** Run the two-arm pre-deployment validation for a scored run. */
export async function runPredeploymentValidation(
  customerId: number,
  runId: number,
): Promise<PredeploymentValidation> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/predeployment/validate`,
    { method: "POST" },
  );
}

/** Fetch the latest pre-deployment validation for a run (or null). */
export async function getPredeploymentValidation(
  customerId: number,
  runId: number,
): Promise<{ validation: PredeploymentValidation | null }> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/predeployment`,
  );
}

export interface ManifestEvent {
  id: number;
  event_type: string;
  run_id: number | null;
  occurred_at: string | null;
  backfilled: boolean;
  reconstruction_source: string | null;
  fields: Record<string, unknown>;
}

/** Fetch the chain-of-custody MANIFEST for a brand (optionally one run). */
export async function getManifest(
  customerId: number,
  runId?: number,
): Promise<{ customer_id: number; events: ManifestEvent[] }> {
  const qs = runId != null ? `?run_id=${runId}` : "";
  return apiFetch(`/onboarding/customers/${customerId}/manifest${qs}`);
}

export async function generateReviewSample(
  customerId: number,
  runId: number,
  seed = 42,
): Promise<ReviewSessionDetail> {
  return apiFetch(
    `/onboarding/customers/${customerId}/runs/${runId}/review/sample`,
    { method: "POST", body: JSON.stringify({ seed }) },
  );
}

export async function getReviewSession(
  sessionId: number,
): Promise<ReviewSessionDetail> {
  return apiFetch(`/onboarding/review/sessions/${sessionId}`);
}

export async function submitReviewItem(
  itemId: number,
  body: {
    human_mentioned: boolean | null;
    human_rv_scores: Record<string, number>;
    reviewer_notes?: string;
  },
): Promise<{ id: number; reviewed: boolean }> {
  return apiFetch(`/onboarding/review/items/${itemId}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function computeSessionKappa(
  sessionId: number,
  requireComplete = false,
): Promise<KappaResult> {
  return apiFetch(`/onboarding/review/sessions/${sessionId}/kappa`, {
    method: "POST",
    body: JSON.stringify({ require_complete: requireComplete }),
  });
}

/**
 * Sprint-3 deliverable: the IRR result is stored in the DB; the HTML report
 * is generated as an export (CSV also available). Compute Kappa first.
 */
export async function downloadKappaExport(
  sessionId: number,
  format: "html" | "csv" = "html",
): Promise<void> {
  return downloadExportFile(
    `/onboarding/review/sessions/${sessionId}/kappa/export?format=${format}`,
    `kappa-session-${sessionId}.${format}`,
    format === "html" ? "text/html" : "text/csv",
  );
}

// ─── Bulk review-item round-trip (offline edit in Excel / Sheets) ────────────

export interface ImportReviewItemsResult {
  session_id: number;
  updated: number;
  skipped: number;
  total_rows: number;
  errors: string[];
  dry_run: boolean;
  committed: boolean;
}

/**
 * Download every review item in this session as a CSV the reviewer can edit
 * offline. Judge columns (read-only references) and editable human columns
 * are pre-filled where the row was reviewed in the UI.
 */
export async function downloadReviewItemsCsv(sessionId: number): Promise<void> {
  return downloadExportFile(
    `/onboarding/review/sessions/${sessionId}/items/export`,
    `review-session-${sessionId}-items.csv`,
    "text/csv",
  );
}

/**
 * Upload an edited review-items CSV. The `item_id` column must be preserved.
 * If any row fails validation, no updates are committed — the response lists
 * every error. Set dryRun=true to validate without writing.
 */
export async function importReviewItemsCsv(
  sessionId: number,
  file: File,
  dryRun = false,
): Promise<ImportReviewItemsResult> {
  const form = new FormData();
  form.append("file", file, file.name);
  const qs = dryRun ? "?dry_run=true" : "";
  return apiFetch(`/onboarding/review/sessions/${sessionId}/items/import${qs}`, {
    method: "POST",
    body: form,
  });
}